/**
 * Оформлення інтерфейсу: тема (день/ніч) і масштаб шрифта (ч.962).
 *
 * Зберігається у ПРОФІЛІ юзера через `uiPrefs` (родина `appearance:`) —
 * тобто налаштування їде за юзером на інший компʼютер/браузер, а не
 * лишається у localStorage однієї машини. localStorage — дзеркало для
 * миттєвого старту (щоб не було «спалаху» світлої теми до відповіді API).
 *
 * Застосування — атрибути на <html>:
 *   data-theme="dark"|"light"  → перевизначення CSS-змінних у styles.css;
 *   data-font="md"|"lg"|"xl"   → базовий font-size (rem-масштаб усього UI).
 *
 * `system` — слухаємо `prefers-color-scheme` ОС і переключаємось на льоту.
 */
import uiPrefs from './uiPrefs';

export type ThemeMode = 'light' | 'dark' | 'system';
// ч.1023: додано `sm` (14px) — власник попросив ще менший крок.
export type FontScale = 'sm' | 'md' | 'lg' | 'xl';

const THEME_KEY = 'appearance:theme';
const FONT_KEY = 'appearance:font';

export const FONT_LABELS: Record<FontScale, string> = {
  sm: 'дрібний', md: 'звичайний', lg: 'більший', xl: 'великий',
};

/** Наступний крок циклічного перемикача розміру шрифта. */
export const nextFont = (f: FontScale): FontScale =>
  (f === 'sm' ? 'md' : f === 'md' ? 'lg' : f === 'lg' ? 'xl' : 'sm');

const mq = (): MediaQueryList | null => {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)');
  } catch { return null; }
};

export const systemPrefersDark = (): boolean => mq()?.matches ?? false;

export function getTheme(): ThemeMode {
  const v = uiPrefs.get<ThemeMode>(THEME_KEY);
  return v === 'dark' || v === 'light' || v === 'system' ? v : 'system';
}

export function getFont(): FontScale {
  const v = uiPrefs.get<FontScale>(FONT_KEY);
  return v === 'sm' || v === 'lg' || v === 'xl' ? v : 'md';
}

/** Фактична тема з урахуванням `system`. */
export const effectiveTheme = (m: ThemeMode = getTheme()): 'light' | 'dark' =>
  (m === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : m);

export function applyAppearance(): void {
  const el = document.documentElement;
  el.setAttribute('data-theme', effectiveTheme());
  el.setAttribute('data-font', getFont());
}

export function setTheme(m: ThemeMode): void {
  uiPrefs.set(THEME_KEY, m);
  applyAppearance();
}

export function setFont(f: FontScale): void {
  uiPrefs.set(FONT_KEY, f);
  applyAppearance();
}

export const subscribeAppearance = (cb: () => void): (() => void) => {
  const unsubTheme = uiPrefs.subscribe(THEME_KEY, cb);
  const unsubFont = uiPrefs.subscribe(FONT_KEY, cb);
  // Зміна теми ОС при режимі `system` — теж перемальовуємо.
  const m = mq();
  const onSys = () => { if (getTheme() === 'system') { applyAppearance(); cb(); } };
  m?.addEventListener?.('change', onSys);
  return () => { unsubTheme(); unsubFont(); m?.removeEventListener?.('change', onSys); };
};

// Застосувати одразу при завантаженні модуля (uiPrefs вже засіяний з
// localStorage) — до першого рендера, без «спалаху» світлої теми.
try { applyAppearance(); } catch { /* SSR/тести без document */ }
